import React, { createContext, useState, useContext, useEffect } from 'react';
import { walletAPI } from '../api/endpoints';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export const WalletProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [balance, setBalance] = useState(0);
  const [totalWinnings, setTotalWinnings] = useState(0);
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [cryptoBalance, setCryptoBalance] = useState(0);
  const [cryptoAddress, setCryptoAddress] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBalance();
      fetchTransactions();
      fetchCryptoBalance();
    }
  }, [isAuthenticated]);

  const fetchBalance = async () => {
    try {
      setLoading(true);
      const response = await walletAPI.getBalance();
      setBalance(response.data.balance || 0);
      setTotalWinnings(response.data.total_winnings || 0);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await walletAPI.getTransactions();
      setTransactions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const fetchCryptoBalance = async () => {
    try {
      const response = await walletAPI.getCryptoBalance?.();
      if (response?.data) {
        setCryptoBalance(response.data.balance || 0);
        setCryptoAddress(response.data.address || null);
      }
    } catch (error) {
      console.error('Failed to fetch crypto balance:', error);
    }
  };

  const fundWallet = async (amount, currency = 'NGN') => {
    try {
      setLoading(true);
      const response = await walletAPI.fund({ amount, currency });
      if (response.data.success) {
        setBalance(response.data.balance);
        toast.success(`Wallet funded successfully!`);
        await fetchTransactions();
        return { success: true, balance: response.data.balance };
      }
      return { success: false, error: 'Funding failed' };
    } catch (error) {
      const message = error.response?.data?.message || 'Funding failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const initializePayment = async (amount, currency = 'NGN') => {
    try {
      setLoading(true);
      const response = await walletAPI.initializePayment({ amount, currency });
      if (response.data.success) {
        return { 
          success: true, 
          link: response.data.link, 
          tx_ref: response.data.tx_ref 
        };
      }
      return { success: false, error: 'Payment initialization failed' };
    } catch (error) {
      const message = error.response?.data?.message || 'Payment initialization failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (transactionId) => {
    try {
      setLoading(true);
      const response = await walletAPI.verifyPayment({ transaction_id: transactionId });
      if (response.data.success) {
        setBalance(response.data.balance);
        toast.success('Payment verified successfully!');
        await fetchTransactions();
        return { success: true, amount: response.data.amount };
      }
      return { success: false, error: 'Verification failed' };
    } catch (error) {
      const message = error.response?.data?.message || 'Verification failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const withdraw = async (amount, bankDetails) => {
    try {
      setLoading(true);
      const response = await walletAPI.withdraw({ amount, bank_details: bankDetails });
      if (response.data.success) {
        setBalance(response.data.new_balance || balance - amount);
        toast.success('Withdrawal initiated successfully!');
        await fetchTransactions();
        return { success: true, reference: response.data.reference };
      }
      return { success: false, error: 'Withdrawal failed' };
    } catch (error) {
      const message = error.response?.data?.message || 'Withdrawal failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    balance,
    totalWinnings,
    loading,
    transactions,
    cryptoBalance,
    cryptoAddress,
    fetchBalance,
    fetchTransactions,
    fetchCryptoBalance,
    fundWallet,
    initializePayment,
    verifyPayment,
    withdraw,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};